import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBars as Menu,
  FaTimes as X,
  FaChevronLeft as ChevronLeft,
  FaChevronRight as ChevronRight,
  FaHeartbeat as HeartPulse,
  FaGraduationCap as GraduationCap,
  FaHandHoldingHeart as HandHeart,
  FaLeaf as Leaf,
  FaUsers as Users,
  FaShieldAlt as ShieldCheck,
  FaArrowRight as ArrowRight,
  FaStar as Sparkles,
  FaCheckCircle as CheckCircle2,
  FaSun as Sun,
  FaStar as Star,
  FaHeartbeat as Activity,
} from "react-icons/fa";
import { FaDownload } from "react-icons/fa6";

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */
const PILLARS = [
  {
    icon: GraduationCap,
    title: "Education",
    desc: "Improving access to quality education and experiential learning with a strong focus on life-skills, hygiene awareness, road safety, and climate education for children and adolescents.",
    stat: "4 Lakh+ students reached",
  },
  {
    icon: HandHeart,
    title: "Skilling & Livelihood",
    desc: "Enhancing employability, entrepreneurship, and livelihood opportunities for youth through structured training, mentoring, and industry linkages.",
    stat: "7,756 students trained",
  },
  {
    icon: HeartPulse,
    title: "Healthcare, Nutrition, Hygiene & Sanitation",
    desc: "Promoting preventive healthcare, early screening, hygiene practices, and nutrition awareness through community health camps, school interventions, and policy dialogue.",
    stat: "450+ Health Camps",
  },
  {
    icon: Leaf,
    title: "Environment & Sustainability",
    desc: "Driving climate action through renewable energy adoption, afforestation, pollution awareness, and community-based sustainability initiatives.",
    stat: "35k+ Saplings planted",
  },
  {
    icon: Users,
    title: "Diversity & Inclusion",
    desc: "Fostering inclusive ecosystems that advance gender equity, empower women, and enhance participation and inclusion of persons with disabilities.",
    stat: "Policy advocacy for PwDs",
  },
];

const STATS = [
  { label: "Health Camps", value: "450+", sub: "5 lakh+ lives impacted" },
  { label: "Students Reached (Edu)", value: "4 Lakh+", sub: "2,100+ teachers trained" },
  { label: "COVID Response", value: "INR 2,400 Cr", sub: "Contributed by ASSOCHAM" },
  { label: "Schools Partnered", value: "300+", sub: "Across 22 States & UTs" },
];

const INTERVENTIONS = [
  {
    title: "Road Safety",
    image: "/images/intervention-education.jpg",
    text: "AFCSR aims to raise public awareness on road safety challenges and promote practical solutions to reduce injuries and fatalities. Through interactive sessions and conferences, we bring together stakeholders to make roads safer for all.",
    icon: ShieldCheck,
  },
  {
    title: "Diversity and Inclusion",
    image: "/images/leadership-2.jpg",
    text: "We create platforms for decision-makers to exchange ideas, discuss policies, and share best practices. AFCSR promotes gender diversity, empowers persons with disabilities, and advocates for equal opportunity policies.",
    icon: Users,
  },
  {
    title: "Education & Life Skills",
    image: "/images/leadership-3.jpg",
    text: "From 2023 to 2025, AFCSR provided adolescents aged 10-19 with essential education on bodily changes, reproductive health, and life skills to help them grow into informed, confident citizens.",
    icon: GraduationCap,
  },
  {
    title: "Healthcare, Hygiene, Sanitation & Nutrition",
    image: "/images/hero-csr.jpg",
    text: "AFCSR promotes Health for All through innovative programs encouraging safe hygiene and healthy nutrition habits. Awareness drives, school workshops, and community camps drive lasting change.",
    icon: Activity,
  },
];

const LEADERS = [
  { name: "Dr. Rajesh Mehta", role: "Chairman, AFCSR", img: "/images/leadership-1.jpg" },
  { name: "Anjali Verma", role: "Director — Programs", img: "/images/leadership-3.jpg" },
  { name: "Prof. Arvind Rao", role: "Advisor — Policy & ESG", img: "/images/leadership-4.jpg" },
  { name: "Neha Patel", role: "Head — Community Engagement", img: "/images/leadership-2.jpg" },
];

/* ------------------------------------------------------------------ */
/*  Small helpers                                                      */
/* ------------------------------------------------------------------ */
function SectionTitle({ eyebrow, title, align = "center" }) {
  return (
    <div className={`max-w-4xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <span className="inline-block mb-4 tracking-[0.2em] text-xs font-semibold uppercase text-[#DB6400]">
        {eyebrow}
      </span>
      <h2 className="font-display text-4xl md:text-6xl font-medium leading-[1.1] text-[#1a1714]">
        {title}
      </h2>
      <div className={`mt-6 w-20 h-[3px] rounded-full bg-gradient-to-r from-[#DA2128] to-[#DB6400] ${align === "center" ? "mx-auto" : ""}`} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
export default function AFCSRPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [hoverPillar, setHoverPillar] = useState(null);
  const sliderRef = useRef(null);
  const galleryStripRef = useRef(null);

  /* Gallery navigation */
  const openGallery = (i) => {
    setGalleryIndex(i);
    setGalleryOpen(true);
    document.body.style.overflow = "hidden";
  };
  const closeGallery = () => {
    setGalleryOpen(false);
    document.body.style.overflow = "";
  };
  const prevGallery = () => setGalleryIndex((i) => (i === 0 ? LEADERS.length - 1 : i - 1));
  const nextGallery = () => setGalleryIndex((i) => (i === LEADERS.length - 1 ? 0 : i + 1));

  /* Auto-slide intervention slider every 4s */
  useEffect(() => {
    const timer = setInterval(() => {
      if (sliderRef.current) {
        const amount = sliderRef.current.clientWidth * 0.85;
        sliderRef.current.scrollBy({ left: amount, behavior: "smooth" });
        setTimeout(() => {
          if (sliderRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
            if (scrollLeft + clientWidth >= scrollWidth - 20) {
              sliderRef.current.scrollTo({ left: 0, behavior: "smooth" });
            }
          }
        }, 500);
      }
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  /* Auto-slide leadership gallery every 4s */
  useEffect(() => {
    const timer = setInterval(() => {
      if (galleryStripRef.current) {
        const amount = galleryStripRef.current.clientWidth * 0.85;
        galleryStripRef.current.scrollBy({ left: amount, behavior: "smooth" });
        setTimeout(() => {
          if (galleryStripRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = galleryStripRef.current;
            if (scrollLeft + clientWidth >= scrollWidth - 20) {
              galleryStripRef.current.scrollTo({ left: 0, behavior: "smooth" });
            }
          }
        }, 500);
      }
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  /* Keyboard for gallery */
  useEffect(() => {
    if (!galleryOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prevGallery();
      if (e.key === "ArrowRight") nextGallery();
      if (e.key === "Escape") closeGallery();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [galleryOpen]);

  /* Scroll slider buttons */
  const scrollSlider = (dir) => {
    if (sliderRef.current) {
      const amount = sliderRef.current.clientWidth * 0.85;
      sliderRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
    }
  };

  const scrollGallery = (dir) => {
    if (galleryStripRef.current) {
      const amount = galleryStripRef.current.clientWidth * 0.85;
      galleryStripRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1a1714]">
      {/* ========================= HEADER ========================= */}


{/* ========================= HERO ========================= */}
<section id="overview" className="relative overflow-hidden">
  <div className="absolute inset-0">
    <img src="/images/hero-viksit.jpg" alt="Community" className="w-full h-full object-cover opacity-90" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#1a1714]/80 via-[#1a1714]/50 to-[#1a1714]/30" />
  </div>

  <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24 lg:py-28">
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="max-w-3xl"
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-1.5 text-sm font-medium text-[#FEF7F4] mb-6 shadow-inner shadow-white/5">
        <Sparkles className="h-4 w-4 text-[#F59C00]" /> Established 2013 · Trust · Social Arm of ASSOCHAM
      </div>
      <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.1] text-white tracking-tight">
        Building <span className="italic font-light text-[#F59C00]">Viksit</span><br />
        <span className="text-[#FEF7F4]">Bharat 2047</span>
      </h1>
      <p className="mt-4 text-sm md:text-base text-[#FEF7F4]/90 leading-relaxed max-w-2xl font-light">
        The ASSOCHAM Foundation for Corporate Social Responsibility bridges public policy and grassroots action — driving inclusive, sustainable development across India.
      </p>
      <div className="mt-8 flex flex-wrap gap-4">
        <a
          href="https://assocham.org/uploads/AFCSR_Brochure_2026.pdf"
          target="_blank"
          className="inline-flex items-center gap-2 rounded-full px-8 py-3 text-base font-semibold text-white bg-[#DA2128]/90 hover:bg-[#DA2128] border border-white/20 backdrop-blur-sm transition-all hover:-translate-y-1"
        >
          AFCSR <FaDownload className="h-5 w-5" />
        </a>
      </div>
    </motion.div>
  </div>

  {/* Decorative wave – reduced height */}
  <div className="absolute bottom-0 left-0 right-0">
    <svg
      viewBox="0 0 1440 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      className="w-full h-10 md:h-16 block"
      aria-hidden="true"
    >
      <path d="M0 60C240 0 480 80 720 40C960 0 1200 70 1440 30V80H0Z" fill="#FAF7F2" />
    </svg>
  </div>
</section>

      {/* ========================= OVERVIEW ========================= */}
      <section className="py-24 lg:py-32 bg-[#FAF7F2]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.7 }}>
              <span className="inline-block mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#DB6400]">Foundation Overview</span>
              <h2 className="font-display text-xl md:text-2xl lg:text-3xl font-medium leading-[1.1] text-[#1a1714] mb-8">
                Rooted in the legacy of <span className="italic">ASSOCHAM</span>
              </h2>
              <div className="space-y-5 text-[#1a1714]/80 leading-relaxed text-sm">
                <p>
                  Established as a Trust in <strong className="text-[#DA2128]">2013</strong>, AFCSR was created to bridge the gap between public policy and grassroots action — delivering impactful programmes in partnership with corporates, government bodies, development organisations, and communities.
                </p>
                <p>
                  With a focus on education, healthcare, skilling, livelihoods, environmental sustainability, climate action, and community empowerment, the Foundation promotes responsible business practices, ESG-led initiatives, and industry–government collaboration.
                </p>
                <p>
                  AFCSR plays a pivotal role in advancing inclusive and sustainable development through well-designed programmes and strategic partnerships aligned with the vision of <strong>Viksit Bharat 2047</strong>.
                </p>
              </div>
              <div className="mt-8 flex gap-3 flex-wrap">
                {["Health Camps", "Solar Energy", "Life Skills", "Afforestation"].map((tag) => (
                  <span key={tag} className="rounded-full bg-[#FEF7F4] border border-[#EFE9DF] px-4 py-1.5 text-sm font-medium text-[#DB6400]">{tag}</span>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#DA2128]/10 rotate-1 hover:rotate-0 transition-transform duration-700">
                <img src="/images/leadership-2.jpg" alt="Collaboration" className="w-full h-[28rem] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1714]/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/10 p-5 text-white">
                    <div className="font-display text-3xl font-medium">5</div>
                    <div className="text-sm opacity-90">Thematic Pillars aligning with national priorities</div>
                  </div>
                </div>
              </div>
              {/* Floating card */}
              <div className="absolute -top-6 -left-6 bg-white rounded-2xl shadow-xl p-4 hidden md:block rotate-[-3deg] hover:rotate-0 transition-transform z-10">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#DA2128]/10 flex items-center justify-center"><Sun className="h-5 w-5 text-[#DA2128]" /></div>
                  <div><div className="text-xs text-[#6e6058]">Solar Installed</div><div className="font-bold text-[#1a1714]">10 Gov. Facilities</div></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================= VISION / MISSION ========================= */}
      <section id="vision" className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute top-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[#DA2128]/5 blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionTitle eyebrow="Our Direction" title="Vision, Our Mission" />

          <div className="mt-16 grid md:grid-cols-2 gap-8 lg:gap-12">
            <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300 }} className="group relative rounded-3xl bg-gradient-to-br from-[#DA2128] to-[#DB2628] p-10 md:p-14 text-white shadow-2xl shadow-[#DA2128]/20 overflow-hidden">
              <div className="absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black/10 to-transparent" />
              <h3 className="font-display text-2xl md:text-3xl mb-4 relative">Our Vision</h3>
              <p className="text-sm leading-relaxed text-white/95 font-light">To be a catalyst for sustainable transformation by fostering collaboration between industry, government, and communities.</p>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#F59C00]">
                <CheckCircle2 className="h-5 w-5" /> Driving inclusive development
              </div>
            </motion.div>

            <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300 }} className="group relative rounded-3xl bg-gradient-to-br from-[#DB6400] to-[#F59C00] p-10 md:p-14 text-white shadow-2xl shadow-[#DB6400]/20 overflow-hidden">
              <div className="absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <h3 className="font-display text-2xl md:text-3xl mb-4 relative">Our Mission</h3>
              <p className="text-sm leading-relaxed text-white/95 font-light">To drive impactful CSR through policy advocacy, meaningful public-private partnerships, sector recognition, and knowledge creation.</p>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#FEF7F4]">
                <CheckCircle2 className="h-5 w-5" /> ESG-led initiatives
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================= THEMATIC PILLARS ========================= */}
      <section id="pillars" className="py-24 lg:py-32 bg-[#FEF7F4]/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionTitle eyebrow="Core Focus" title="Thematic Pillars" />
          <p className="max-w-3xl mx-auto text-center text-[#1a1714]/70 text-sm mt-6 mb-16">Our work is anchored around five core thematic pillars, aligned with national priorities — creating measurable outcomes across health, education, livelihoods, environment, and inclusion.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.a
                  key={p.title}
                  href="#interventions"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  onMouseEnter={() => setHoverPillar(i)}
                  onMouseLeave={() => setHoverPillar(null)}
                  className={`group relative rounded-3xl bg-white p-8 md:p-10 shadow-xl shadow-[#DA2128]/5 border border-[#EFE9DF] transition-all duration-300 ${hoverPillar === i ? "-translate-y-2 shadow-2xl shadow-[#DA2128]/10" : ""}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg transition-colors ${hoverPillar === i ? "bg-[#DA2128] text-white shadow-[#DA2128]/20" : "bg-[#FEF7F4] text-[#DA2128]"}`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    {/* <span className="font-display text-3xl text-[#EFE9DF]/60 group-hover:text-[#DA2128]/20 transition-colors">0{i + 1}</span> */}
                  </div>
                  <h3 className="font-display text-lg md:text-xl mb-3 text-[#1a1714] group-hover:text-[#DA2128] transition-colors">{p.title}</h3>
                  <p className="text-[#1a1714]/70 leading-relaxed mb-5">{p.desc}</p>
                  <div className="inline-flex items-center gap-2 text-sm font-bold text-[#DB6400] bg-[#FEF7F4] px-3 py-1.5 rounded-full">
                    <Star className="h-3.5 w-3.5" /> {p.stat}
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================= STATS ========================= */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-[#1a1714] to-[#2d2622] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="none"><path d="M0 100 C300 200 500 0 800 150 L800 400 L0 400 Z" fill="#DA2128" /></svg>
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 relative">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {STATS.map((s, i) => (
              <motion.div key={s.label} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: i * 0.1 }} className="rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10 hover:bg-white/10 transition-colors">
                <div className="font-display text-3xl md:text-4xl font-medium text-[#F59C00] mb-3">{s.value}</div>
                <div className="font-semibold text-lg">{s.label}</div>
                <div className="text-white/60 text-sm mt-1">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= KEY AREAS SLIDER ========================= */}
      <section id="interventions" className="py-24 lg:py-32 bg-[#FAF7F2] relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionTitle eyebrow="Key Areas" title="Areas of Intervention" />
          <p className="max-w-3xl mx-auto text-center text-[#1a1714]/70 text-lg mt-6 mb-14">Focused initiatives delivering measurable impact across critical social and environmental challenges.</p>

          {/* Slider controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-2">
              <button onClick={() => scrollSlider("left")} aria-label="Previous" className="h-12 w-12 rounded-full bg-[#DA2128] text-white flex items-center justify-center shadow-lg shadow-[#DA2128]/20 hover:scale-105 transition-transform"><ChevronLeft /></button>
              <button onClick={() => scrollSlider("right")} aria-label="Next" className="h-12 w-12 rounded-full bg-[#DA2128] text-white flex items-center justify-center shadow-lg shadow-[#DA2128]/20 hover:scale-105 transition-transform"><ChevronRight /></button>
            </div>
            <div className="text-sm font-medium text-[#6e6058]">Swipe or click arrows to explore</div>
          </div>

          <div ref={sliderRef} className="flex gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-6 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide">
            {INTERVENTIONS.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="snap-start shrink-0 w-[85vw] md:w-[70vw] lg:w-[45rem] rounded-3xl bg-white border border-[#EFE9DF] shadow-xl shadow-[#DA2128]/5 overflow-hidden group hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="relative h-72 md:h-80 overflow-hidden">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#DA2128] text-white px-3 py-1 text-xs font-bold shadow-lg shadow-black/20"><Icon className="h-3.5 w-3.5" /> {item.title}</span>
                    </div>
                  </div>
                  <div className="p-8 md:p-10">
                    <h3 className="font-display text-xl md:text-lg mb-2 text-[#1a1714]">{item.title}</h3>
                    <p className="text-[#1a1714]/80 leading-relaxed text-lg">{item.text}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================= LEADERSHIP GALLERY ========================= */}
      <section id="leadership" className="py-24 lg:py-32 bg-[#FEF7F4]/50 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#DA2128]/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="inline-block mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#DB6400]">People Behind Purpose</span>
              <h2 className="font-display text-3xl md:text-4xl font-medium leading-[1.1] text-[#1a1714]">Foundation <span className="italic">Leadership</span></h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scrollGallery("left")} aria-label="Previous" className="h-12 w-12 rounded-full bg-white border border-[#EFE9DF] text-[#1a1714] flex items-center justify-center shadow-sm hover:border-[#DA2128] hover:text-[#DA2128] transition-colors"><ChevronLeft /></button>
              <button onClick={() => scrollGallery("right")} aria-label="Next" className="h-12 w-12 rounded-full bg-white border border-[#EFE9DF] text-[#1a1714] flex items-center justify-center shadow-sm hover:border-[#DA2128] hover:text-[#DA2128] transition-colors"><ChevronRight /></button>
            </div>
          </div>

          {/* Gallery strip */}
          <div ref={galleryStripRef} className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide">
            {LEADERS.map((l, i) => (
              <button
                key={l.name}
                onClick={() => openGallery(i)}
                className="snap-start shrink-0 group text-left w-72 md:w-80 bg-white rounded-3xl shadow-lg shadow-[#DA2128]/5 border border-[#EFE9DF] overflow-hidden hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl hover:shadow-[#DA2128]/10"
              >
                <div className="relative h-80 overflow-hidden">
                  <img src={l.img} alt={l.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1714]/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-display text-2xl text-white leading-none">{l.name}</h3>
                    <p className="text-white/80 text-sm font-medium mt-1">{l.role}</p>
                  </div>
                  <div className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Modal */}
        <AnimatePresence>
          {galleryOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-center justify-center bg-[#1a1714]/80 backdrop-blur-xl p-6"
              onClick={closeGallery}
            >
              <button onClick={closeGallery} className="absolute top-6 right-6 h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition-colors z-10" aria-label="Close"><X /></button>

              <motion.div
                key={galleryIndex}
                initial={{ scale: 0.92, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.92, opacity: 0, y: 20 }}
                transition={{ duration: 0.35 }}
                className="relative max-w-5xl w-full bg-[#FEF7F4] rounded-3xl overflow-hidden shadow-2xl shadow-black/30"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="grid lg:grid-cols-2">
                  <div className="relative h-80 lg:h-[32rem]">
                    <img src={LEADERS[galleryIndex].img} alt={LEADERS[galleryIndex].name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#FEF7F4] hidden lg:block" />
                  </div>
                  <div className="p-8 md:p-12 lg:p-14 flex flex-col justify-center">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#DB6400] mb-3">Leadership Profile</div>
                    <h3 className="font-display text-2xl md:text-3xl text-[#1a1714] mb-2">{LEADERS[galleryIndex].name}</h3>
                    <p className="text-[#DB6400] font-semibold text-lg mb-6">{LEADERS[galleryIndex].role}</p>
                    <p className="text-[#1a1714]/70 leading-relaxed text-lg mb-8">A dedicated leader shaping AFCSR&apos;s strategic direction, building partnerships, and driving measurable social impact across health, education, skilling, and sustainability programs nationwide.</p>
                    <div className="flex gap-3">
                      <button onClick={prevGallery} className="h-12 w-12 rounded-full bg-[#DA2128] text-white flex items-center justify-center shadow-lg shadow-[#DA2128]/20 hover:scale-105 transition-transform" aria-label="Previous"><ChevronLeft /></button>
                      <button onClick={nextGallery} className="h-12 w-12 rounded-full bg-[#DA2128] text-white flex items-center justify-center shadow-lg shadow-[#DA2128]/20 hover:scale-105 transition-transform" aria-label="Next"><ChevronRight /></button>
                      <div className="ml-auto flex items-center gap-2 text-sm font-medium text-[#6e6058]">{galleryIndex + 1} / {LEADERS.length}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

     

      {/* ========================= FOOTER ========================= */}

    </div>
  );
}
